import React from 'react';
import { Button, Divider, Empty, Form, Select, Typography } from 'antd';
import type {
  JobChainTemplateGraph,
  JobChainTemplateMetadata,
} from '@/api/JobChainTemplates';
import { EdgeConditionsEditor } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/EdgeConditionsEditor';
import { supportedConditionOperators } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/graphSupport';
import { useTranslate } from '@/translations';

export const GraphProperties = ({
  graph,
  metadata,
  editable,
  selection,
  label,
  onSelect,
  onChange,
}: {
  graph: JobChainTemplateGraph;
  metadata: JobChainTemplateMetadata;
  editable: boolean;
  selection: string | null;
  label: (key: string) => string;
  onSelect: (key: string | null) => void;
  onChange: (graph: JobChainTemplateGraph) => void;
}) => {
  const { t } = useTranslate();
  const selectedEdge = graph.edges.find((edge) => edge.key === selection);
  const selectedNode = graph.nodes.find((node) => node.key === selection);
  const fields = metadata.conditionFields.filter(
    (field) =>
      field.valueType === 'string' &&
      field.operators.length &&
      field.operators.every((operator) =>
        supportedConditionOperators.includes(operator),
      ),
  );
  return (
    <section aria-label={t('jobChainTemplateGraphSelectedProperties')}>
      <Typography.Title level={5}>
        {t('jobChainTemplateGraphProperties')}
      </Typography.Title>
      {!selection && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('jobChainTemplateGraphSelectElement')}
        />
      )}
      {selectedNode && (
        <>
          <Typography.Paragraph strong>
            {label(selectedNode.key)}
          </Typography.Paragraph>
          <Form layout="vertical" size="small">
            <Form.Item label={t('jobChainTemplateGraphJoinPolicy')}>
              <Select<'AnyLive' | 'AllLive'>
                aria-label={t('jobChainTemplateGraphJoinPolicy')}
                value={selectedNode.joinPolicy}
                disabled={!editable}
                options={[
                  {
                    value: 'AnyLive',
                    label: t('jobChainTemplateGraphJoinAnyLive'),
                  },
                  {
                    value: 'AllLive',
                    label: t('jobChainTemplateGraphJoinAllLive'),
                  },
                  ...(!['AnyLive', 'AllLive'].includes(selectedNode.joinPolicy)
                    ? [
                        {
                          value: selectedNode.joinPolicy,
                          label: String(selectedNode.joinPolicy),
                        },
                      ]
                    : []),
                ]}
                onChange={(joinPolicy) => {
                  if (editable)
                    onChange({
                      ...graph,
                      nodes: graph.nodes.map((node) =>
                        node.key === selectedNode.key
                          ? { ...node, joinPolicy }
                          : node,
                      ),
                    });
                }}
              />
            </Form.Item>
            <Form.Item label={t('jobChainTemplateGraphDeletedMarker')}>
              <Select
                aria-label={t('jobChainTemplateGraphDeletedMarker')}
                value={String(selectedNode.deleted)}
                disabled={!editable}
                options={[
                  { value: 'null', label: t('jobChainTemplateGraphNotSet') },
                  { value: 'false', label: t('jobChainTemplateGraphNo') },
                  { value: 'true', label: t('jobChainTemplateGraphYes') },
                ]}
                onChange={(value) => {
                  if (editable)
                    onChange({
                      ...graph,
                      nodes: graph.nodes.map((node) =>
                        node.key === selectedNode.key
                          ? {
                              ...node,
                              deleted:
                                value === 'null' ? null : value === 'true',
                            }
                          : node,
                      ),
                    });
                }}
              />
            </Form.Item>
          </Form>
        </>
      )}
      {selectedEdge && (
        <>
          <Typography.Text type="secondary">
            {t('jobChainTemplateGraphConnection')}
          </Typography.Text>
          <Typography.Paragraph strong className="connection-summary">
            {label(selectedEdge.fromKey)} → {label(selectedEdge.toKey)}
          </Typography.Paragraph>
          <Button
            type="link"
            size="small"
            onClick={() => onSelect(selectedEdge.toKey)}
          >
            {t('jobChainTemplateGraphTargetNodeSettings')}
          </Button>
          <Divider style={{ margin: '12px 0' }} />
          <EdgeConditionsEditor
            edge={selectedEdge}
            fields={fields}
            editable={editable}
            onChange={(next) => {
              if (editable)
                onChange({
                  ...graph,
                  edges: graph.edges.map((edge) =>
                    edge.key === next.key ? next : edge,
                  ),
                });
            }}
          />
        </>
      )}
    </section>
  );
};
